<template>
    <v-row no-gutters>
        <v-col cols="4">
            <v-card flat class="mx-auto" tile>
                <v-card-title>Two Factor Authentication</v-card-title>
                <v-card-subtitle>Add additional security to your account using two factor authentication.</v-card-subtitle>
            </v-card>
        </v-col>
        <v-col cols="8" class="justify-center">
            <v-form>
                <v-card flat>
                    <v-card-text>
                        <h3 v-if="twoFactorEnabled && ! confirming" class="text-lg font-medium text-gray-900">
                            You have enabled two factor authentication.
                        </h3>

                        <h3 v-else-if="twoFactorEnabled && confirming" class="text-lg font-medium text-gray-900">
                            Finish enabling two factor authentication.
                        </h3>

                        <h3 v-else class="text-lg font-medium text-gray-900">
                            You have not enabled two factor authentication.
                        </h3>

                        <div class="mt-3 max-w-xl text-sm text-gray-600">
                            <p>
                                When two factor authentication is enabled, you will be prompted for a secure, random token during authentication. You may retrieve this token from your phone's Google Authenticator application.
                            </p>
                        </div>

                        <div v-if="twoFactorEnabled">
                            <div v-if="qrCode">
                                <div class="mt-4 max-w-xl text-sm text-gray-600">
                                    <p v-if="confirming" class="font-semibold">
                                        To finish enabling two factor authentication, scan the following QR code using your phone's authenticator application or enter the setup key and provide the generated OTP code.
                                    </p>

                                    <p v-else>
                                        Two factor authentication is now enabled. Scan the following QR code using your phone's authenticator application or enter the setup key.
                                    </p>
                                </div>

                                <div class="mt-4" v-html="qrCode" />

                                <div class="mt-4 max-w-xl text-sm text-gray-600" v-if="setupKey">
                                    <p class="font-semibold">
                                        Setup Key: <span v-html="setupKey"></span>
                                    </p>
                                </div>

                                <div v-if="confirming" class="mt-4">
                                    <v-text-field
                                        label="Code"
                                        id="code"
                                        v-model="confirmationForm.code"
                                        type="text"
                                        name="code"
                                        class="block mt-1 w-1/2"
                                        inputmode="numeric"
                                        autofocus
                                        autocomplete="one-time-code"
                                        @keyup.enter="confirmTwoFactorAuthentication"
                                        :error-messages="confirmTwoFactorAuthenticationErrorMessages"
                                    />

                                    <!-- <JetInputError :message="confirmationForm.errors.code" class="mt-2" /> -->
                                </div>
                            </div>

                            <div v-if="recoveryCodes.length > 0 && ! confirming">
                                <div class="mt-4 max-w-xl text-sm text-gray-600">
                                    <p class="font-semibold">
                                        Store these recovery codes in a secure password manager. They can be used to recover access to your account if your two factor authentication device is lost.
                                    </p>
                                </div>

                                <div class="grid gap-1 max-w-xl mt-4 px-4 py-4 font-mono text-sm bg-gray-100 rounded-lg">
                                    <div v-for="code in recoveryCodes" :key="code">
                                        {{ code }}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="mt-5">
                            <div v-if="! twoFactorEnabled">
                                <confirms-password
                                    @confirmed="enableTwoFactorAuthentication"
                                />
                            </div>

                            <div v-else>
                                <confirms-password
                                    v-if="confirming"
                                    @confirmed="confirmTwoFactorAuthentication"
                                >
                                    <template v-slot="{ on, attrs }">
                                        <v-btn
                                            color="primary"
                                            class="mr-3"
                                            v-bind="attrs"
                                            v-on="on"
                                            :disabled="enabling"
                                        >
                                            Confirm
                                        </v-btn>
                                    </template>
                                </confirms-password>

                                <confirms-password
                                    v-if="recoveryCodes.length > 0 && ! confirming"
                                    @confirmed="regenerateRecoveryCodes"
                                >
                                    <v-btn
                                        v-slot="{ on, attrs }"
                                        class="mr-3"
                                        color="green"
                                    >
                                        Regenerate Recovery Codes
                                    </v-btn>
                                </confirms-password>

                                <confirms-password
                                    v-if="recoveryCodes.length === 0 && ! confirming"
                                    @confirmed="showRecoveryCodes"
                                >
                                    <v-btn
                                        v-slot="{ on, attrs }"
                                        class="mr-3"
                                        color="success"
                                    >
                                        Show Recovery Codes
                                    </v-btn>
                                </confirms-password>

                                <confirms-password
                                    v-if="confirming"
                                    @confirmed="disableTwoFactorAuthentication"
                                >
                                    <v-btn
                                        text
                                        v-slot="{ on, attrs }"
                                        :class="{ 'opacity-25': disabling }"
                                        :disabled="disabling"
                                    >
                                        Cancel
                                    </v-btn>
                                </confirms-password>

                                <confirms-password
                                    v-if="! confirming"
                                    @confirmed="disableTwoFactorAuthentication"
                                >
                                    <v-btn
                                        color="deep-orange"
                                        :class="{ 'opacity-25': disabling }"
                                        :disabled="disabling"
                                    >
                                        Disable
                                    </v-btn>
                                </confirms-password>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
            </v-form>
        </v-col>
    </v-row>
</template>

<script>
import _ from 'lodash';
import ConfirmsPassword from "@/Shared/Dialogs/ConfirmsPassword";

export default {
    components: {ConfirmsPassword},
    props: {
        requiresConfirmation: {
            type: Boolean,
            default: true,
        }
    },

    data() {
        return {
            enabling: false,
            confirming: false,
            disabling: false,
            qrCode: null,
            setupKey: null,
            recoveryCodes: [],
            confirmationForm: this.$inertia.form({
                code: '',
            }),
        };
    },

    methods: {
        enableTwoFactorAuthentication() {
            this.enabling = true;

            this.$inertia.post('/user/two-factor-authentication', {}, {
                preserveScroll: true,
                onSuccess: () => Promise.all([
                    this.showQrCode(),
                    this.showSetupKey(),
                    this.showRecoveryCodes(),
                ]),
                onFinish: () => {
                    this.enabling = false;
                    this.confirming = this.requiresConfirmation;
                },
            });
        },

        showQrCode() {
            return axios.get('/user/two-factor-qr-code').then(response => {
                this.qrCode = response.data.svg;
            });
        },

        showSetupKey() {
            return axios.get('/user/two-factor-secret-key').then(response => {
                this.setupKey = _.get(response, 'data.secretKey', null);
            });
        },

        showRecoveryCodes() {
            return axios.get('/user/two-factor-recovery-codes').then(response => {
                this.recoveryCodes = response.data;
            });
        },

        confirmTwoFactorAuthentication() {
            this.confirmationForm.post('/user/confirmed-two-factor-authentication', {
                errorBag: "confirmTwoFactorAuthentication",
                preserveScroll: true,
                preserveState: true,
                onSuccess: () => {
                    this.confirming = false;
                    this.qrCode = null;
                    this.setupKey = null;
                },
            });
        },

        regenerateRecoveryCodes() {
            axios
                .post('/user/two-factor-recovery-codes')
                .then(() => this.showRecoveryCodes());
        },

        disableTwoFactorAuthentication() {
            this.disabling = true;

            this.$inertia.delete('/user/two-factor-authentication', {
                preserveScroll: true,
                onSuccess: () => {
                    this.disabling = false;
                    this.confirming = false;
                },
            });
        },
    },

    computed: {
        twoFactorEnabled() {
            return ! this.enabling && _.get(this.$page, 'props.user.two_factor_enabled', false);
        },

        confirmTwoFactorAuthenticationErrorMessages() {
            return _.get(this.$page.props, 'errorBags.confirmTwoFactorAuthentication.code', []);
        },
    },

    watch: {
        twoFactorEnabled() {
            if (! this.twoFactorEnabled) {
                this.confirmationForm.reset();
                this.confirmationForm.clearErrors();
            }
        },
    }
}
</script>

<style scoped>

</style>
